import { useTranslation } from "@redotech/redo-i18n";
import { RedoFileUpload } from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export interface FileWithDescription {
  file: File;
  description: string;
}

interface PhotoUploadSectionProps {
  filesWithDescriptions: FileWithDescription[];
  onFilesAdded: (files: File[]) => void;
  onFileDeleted: (index: number) => void;
  onDescriptionChange: (index: number, description: string) => void;
  required?: boolean;
  maxFiles?: number;
  compact?: boolean;
}

export const PhotoUploadSection = memo(function PhotoUploadSection({
  filesWithDescriptions,
  onFilesAdded,
  onFileDeleted,
  onDescriptionChange,
  required = false,
  maxFiles = 5,
  compact = false,
}: PhotoUploadSectionProps) {
  const { t } = useTranslation();
  const handleRedoFileUploadFilesAdded = (files: FileList | null) => {
    if (files) {
      onFilesAdded(Array.from(files));
    }
  };

  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        {required ? t("Photos (required)") : t("Photos (optional)")}
      </Text>
      <Flex dir="column" gap="xs">
        <RedoFileUpload
          accept="image/*"
          acceptMessage={t("SVG, PNG, JPG or GIF (max. 800x400px)")}
          files={filesWithDescriptions.map((item) => ({
            name: item.file.name,
            bytesTotal: item.file.size,
            value: item.file,
          }))}
          fullWidth
          handleFileDeleted={onFileDeleted}
          handleFilesAdded={handleRedoFileUploadFilesAdded}
          nFilesToAccept={maxFiles}
          {...(compact && { compact: true })}
        />
        {filesWithDescriptions.length > 0 && (
          <Flex dir="column" gap="xs">
            {filesWithDescriptions.map((item, index) => (
              <Flex dir="column" gap="xxs" key={index}>
                <Text fontSize="xs" textColor="secondary">
                  {item.file.name}
                </Text>
                <RedoTextInput
                  placeholder={t("Photo description (optional)")}
                  setValue={(value) => onDescriptionChange(index, value)}
                  value={item.description}
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
